import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  TENANT_ROLES,
  type CreateInvitationResponse,
  type InvitationSummary,
  type TenantRole,
  type UserSummary,
} from "@hyphesync/shared";
import { describeError, useApi } from "../lib/api";
import { useMe } from "../lib/session";
import { roleLabel } from "../lib/labels";

const ROLE_HELP: Record<TenantRole, string> = {
  admin: "Everything, including users and connector settings",
  manager: "Everything except users and connector credentials",
  viewer: "Read-only dashboards and exports",
};

const dateFmt = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const fmtDate = (iso: string | null) => (iso ? dateFmt.format(new Date(iso)) : "Never");

const inputClass = "touch-target rounded-lg border border-line bg-card px-3 py-2 text-ink";

/** Admin-only (the API enforces it; the nav hides the link for everyone else). */
export function Users() {
  const api = useApi();
  const me = useMe();
  const [users, setUsers] = useState<UserSummary[] | null>(null);
  const [invites, setInvites] = useState<InvitationSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([
        api<{ users: UserSummary[] }>("GET", "/api/users"),
        api<{ invitations: InvitationSummary[] }>("GET", "/api/invitations"),
      ]);
      setUsers(u.users);
      setInvites(i.invitations);
      setLoadError(null);
    } catch (e) {
      setLoadError(describeError(e));
    }
  }, [api]);

  useEffect(() => {
    void load(); // load() catches everything itself
  }, [load]);

  async function run(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setActionError(describeError(e));
    } finally {
      setBusy(null);
    }
  }

  // Both changes take effect at once and can lock someone out or grant admin, so both confirm.
  // A select fires change on a keyboard arrow (Chromium on Windows), so without this an arrow key
  // on a focused role menu re-roles the person.
  const updateUser = (u: UserSummary, patch: { role?: TenantRole; status?: "active" | "disabled" }) => {
    const who = u.name ? `${u.name} (${u.email})` : u.email;
    const question = patch.role
      ? `Change ${who} from ${roleLabel(u.role)} to ${roleLabel(patch.role)}?`
      : patch.status === "disabled"
        ? `Disable ${who}? They lose access immediately.`
        : `Enable ${who}? They get access again as ${roleLabel(u.role)}.`;
    if (!window.confirm(question)) return;
    void run(`user:${u.id}`, () => api("PATCH", `/api/users/${u.id}`, patch));
  };

  const revoke = (inv: InvitationSummary) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}? Its link stops working.`)) return;
    void run(`inv:${inv.id}`, () => api("DELETE", `/api/invitations/${inv.id}`));
  };

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Users</h1>
        <p className="mt-1 text-muted">People with access to {me.tenant.name}.</p>
      </div>

      {loadError && (
        <p role="alert" className="rounded-lg border border-line bg-card p-4 text-bad">
          {loadError}{" "}
          <button type="button" onClick={() => void load()} className="touch-target font-medium text-brand underline">Retry</button>
        </p>
      )}
      {actionError && <p role="alert" className="text-bad">{actionError}</p>}

      <InviteForm onInvited={load} />

      <section aria-labelledby="members" className="flex flex-col gap-3">
        <h2 id="members" className="text-lg font-semibold">Members</h2>
        {users === null && !loadError && <p className="text-muted">Loading…</p>}
        {users && (
          <div className="overflow-x-auto rounded-xl border border-line bg-card">
            <table className="w-full min-w-[40rem] text-left text-sm">
              <thead className="border-b border-line text-muted">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">Person</th>
                  <th scope="col" className="px-4 py-3 font-medium">Role</th>
                  <th scope="col" className="px-4 py-3 font-medium">Status</th>
                  <th scope="col" className="px-4 py-3 font-medium">Last seen</th>
                  <th scope="col" className="px-4 py-3 font-medium"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const self = u.id === me.user.id;
                  const disabled = busy !== null || self;
                  return (
                    <tr key={u.id} className="border-b border-line last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="font-medium">{u.name || u.email}{self && <span className="text-muted"> (you)</span>}</div>
                        <div className="text-muted">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          aria-label={`Role for ${u.email}`}
                          value={u.role}
                          disabled={disabled}
                          onChange={(e) => updateUser(u, { role: e.target.value as TenantRole })}
                          className={`${inputClass} disabled:opacity-50`}
                        >
                          {TENANT_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span className={u.status === "active" ? "text-ok" : "text-muted"}>
                          {u.status === "active" ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted">{fmtDate(u.lastSeenAt)}</td>
                      <td className="px-4 py-3 text-right">
                        {!self && (
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => updateUser(u, { status: u.status === "active" ? "disabled" : "active" })}
                            className="touch-target rounded-lg border border-line px-3 py-1.5 font-medium text-ink disabled:opacity-50"
                          >
                            {u.status === "active" ? "Disable" : "Enable"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="pending" className="flex flex-col gap-3">
        <h2 id="pending" className="text-lg font-semibold">Pending invitations</h2>
        {invites.length === 0 ? (
          <p className="text-muted">None.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-xl border border-line bg-card">
            {invites.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                <div>
                  <div className="font-medium">{inv.email}</div>
                  <div className="text-muted">{roleLabel(inv.role)} · expires {fmtDate(inv.expiresAt)}</div>
                </div>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => revoke(inv)}
                  className="touch-target rounded-lg border border-line px-3 py-1.5 font-medium text-ink disabled:opacity-50"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function InviteForm({ onInvited }: { onInvited: () => Promise<void> }) {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("viewer");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateInvitationResponse | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSending(true);
    setError(null);
    setResult(null);
    setCopied(false);
    try {
      setResult(await api<CreateInvitationResponse>("POST", "/api/invitations", { email, role }));
      setEmail("");
      await onInvited();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSending(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false); // the link stays visible and selectable
    }
  }

  return (
    <section aria-labelledby="invite" className="rounded-xl border border-line bg-card p-5">
      <h2 id="invite" className="text-lg font-semibold">Invite someone</h2>
      <p className="mt-1 text-sm text-muted">They sign in as this address, with Google or a one-time code emailed to it (no password is ever set). For an address that isn't a Google account, HypheSync support must also enable it in Auth0 before they can sign in.</p>
      <form onSubmit={(e) => void submit(e)} className="mt-4 flex flex-wrap items-end gap-3">
        <label className="flex min-w-0 flex-1 basis-64 flex-col gap-1 text-sm font-medium">
          Email
          <input
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputClass} min-w-0 font-normal`}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">
          Role
          <select value={role} onChange={(e) => setRole(e.target.value as TenantRole)} className={`${inputClass} font-normal`}>
            {TENANT_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </label>
        <button
          type="submit"
          disabled={sending}
          className="touch-target rounded-lg bg-brand px-5 py-2.5 font-medium text-on-brand disabled:opacity-50"
        >
          {sending ? "Sending…" : "Send invitation"}
        </button>
      </form>
      <p className="mt-2 text-sm text-muted">{ROLE_HELP[role]}.</p>
      {error && <p role="alert" className="mt-3 text-bad">{error}</p>}
      {result && (
        <div role="status" className="mt-4 flex flex-col gap-2 rounded-lg border border-line bg-surface p-4 text-sm">
          <p>
            {result.emailSent
              ? `Invitation emailed to ${result.invitation.email}. You can also share this link with them:`
              : `The email couldn't be sent. Copy this link and send it to ${result.invitation.email} yourself:`}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded bg-card px-2 py-1">{result.inviteUrl}</code>
            <button
              type="button"
              onClick={() => void copy(result.inviteUrl)}
              className="touch-target rounded-lg border border-line px-3 py-1.5 font-medium text-ink"
            >
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <p className="text-muted">The link works once and expires in 30 days. It won't be shown again.</p>
        </div>
      )}
    </section>
  );
}
